import type {
  ProfilEspace,
  SetRoleResult,
  SpaceInvitationView,
  SpaceMemberView,
  SpaceOverview,
  SpaceRole,
} from '@authsemply/shared';
import { api } from './api';

/**
 * L'espace, côté portail.
 *
 * `/espace/*` et non `/accounts/*` : les deux servent le même service, mais
 * `/accounts/*` exige un access token OIDC — ce que le portail n'a pas. Ici,
 * c'est la session SSO (cookie httpOnly) qui authentifie, exactement comme
 * `/auth/me`.
 *
 * Aucune de ces fonctions ne prend d'identifiant d'espace : l'API répond
 * toujours dans le périmètre de l'appelant. C'est ce qui rend l'énumération
 * sans objet, et il ne faut pas le défaire en ajoutant un paramètre ici.
 */

export const apiEspace = {
  apercu: () => api.get<SpaceOverview>('/espace'),
  /**
   * Un espace de plus, dont le compte devient propriétaire (29/10/2026) : un
   * espace = un payeur, c'est ainsi qu'on facture une autre société à part.
   * La seule exception à la règle ci-dessus — elle CRÉE un espace, elle n'en
   * désigne aucun d'existant.
   */
  creerEspace: (name: string) => api.post<{ id: string; name: string; slug: string }>('/espace/espaces', { name }),
  membres: () => api.get<SpaceMemberView[]>('/espace/membres'),
  invitations: () => api.get<SpaceInvitationView[]>('/espace/invitations'),

  changerRole: (userId: string, role: SpaceRole) =>
    api.patch<SetRoleResult>(`/espace/membres/${encodeURIComponent(userId)}`, { role }),

  retirerMembre: (userId: string) =>
    api.delete<{ removed: true }>(`/espace/membres/${encodeURIComponent(userId)}`),

  /** `profil` (04/10/2026) : `LECTURE_SEULE` pour un expert-comptable, un associé… */
  inviter: (email: string, role: Exclude<SpaceRole, 'OWNER'>, profil: ProfilEspace = 'STANDARD') =>
    api.post<SpaceInvitationView>('/espace/invitations', { email, role, profil }),

  /** Change le seul profil d'un simple membre (lecture seule ↔ standard). */
  changerProfil: (userId: string, profil: ProfilEspace) =>
    api.patch<{ profil: ProfilEspace }>(`/espace/membres/${encodeURIComponent(userId)}`, { profil }),

  annulerInvitation: (id: string) =>
    api.delete<{ revoked: true }>(`/espace/invitations/${encodeURIComponent(id)}`),

  /** Rejoindre l'espace qui invite, avec le compte connecté (04/10/2026, ZO-AUTH-01). */
  accepterInvitation: (token: string) =>
    api.post<{ joined: true; profil?: ProfilEspace }>('/espace/invitations/accept', { token }),
};

/** Nom lisible d'un membre : le nom affiché s'il existe, l'adresse sinon. */
export function nomMembre(membre: SpaceMemberView): string {
  return membre.user.displayName?.trim() || membre.user.email;
}
